import React, { useMemo } from 'react';
import { Loader2, LucideIcon, Package } from 'lucide-react';
import ProductCard from '@/domains/products/components/ProductCard';
import { Product } from '@/shared/types/productTypes';
import { Button } from '@/shared/ui/button';
import { useInfiniteAccountProducts } from '@/domains/products/services/account-products';
import EmptyView from '@/shared/components/EmptyView';
import { ServiceLine } from '@/shared/types/serviceLineTypes';
import SearchBar from '@/shared/components/SearchBar';

interface SubscriptionsTabProps {
  onManageServiceLine: (serviceLine: ServiceLine) => void;
}

export const SubscriptionsTab = ({ onManageServiceLine }: SubscriptionsTabProps) => {

  const {
    data: productsInfiniteData,
    isLoading: isLoadingProducts,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteAccountProducts();

  const products = useMemo(() => {
    const pages = productsInfiniteData?.pages ?? [];
    const seen = new Set<string>();
    const merged: Product[] = [];
    for (const page of pages) {
      for (const p of page.results ?? []) {
        const id = p.orbiusProductReferenceId;
        if (id && !seen.has(id)) {
          seen.add(id);
          merged.push(p);
        }
      }
    }
    return merged;
  }, [productsInfiniteData?.pages]);

  const totalCount = productsInfiniteData?.pages[0]?.totalCount ?? 0;
  const [searchTerm, setSearchTerm] = React.useState('');

  const filteredProducts = useMemo(() => {
    if (!searchTerm) return products;
    const searchLower = searchTerm.toLowerCase();
    return products.filter(product =>
      product.orbiusProductName?.toLowerCase().includes(searchLower)
    );
  }, [products, searchTerm]);

  const renderResourceGrid = (items: Product[], renderItem: (item: Product) => React.ReactNode, EmptyIcon: LucideIcon, emptyTitle: string, emptyDescription: string) => {
    if (items.length === 0) {
      return (
        <EmptyView
          icon={EmptyIcon}
          title={emptyTitle}
          description={emptyDescription}
        />
      );
    }

    return (
      <div className="flex-1 overflow-y-auto space-y-4">
        <div className="space-y-4">
          {items.map(renderItem)}
        </div>

        <div className="glass-panel flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between rounded-lg">
          <div className="flex min-w-0 flex-col gap-0.5 sm:flex-row sm:items-center sm:gap-2">
            <span className="text-md">
              Loaded <span className="font-medium">{products.length}</span>
              {' '}of <span className="font-bold">{totalCount}</span> subscriptions
            </span>
            {filteredProducts.length !== products.length && (
              <span className="text-sm">
                ({filteredProducts.length} match current search query)
              </span>
            )}
          </div>
          {hasNextPage ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="shrink-0 gap-2 self-start sm:self-auto"
              disabled={isFetchingNextPage}
              onClick={() => void fetchNextPage()}
            >
              {isFetchingNextPage ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading…
                </>
              ) : (
                'Load more'
              )}
            </Button>
          ) : null}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Header Section */}
      <div className="p-4 flex items-center justify-between rounded-lg border border-border bg-muted">
        <div>
          <h1 className="text-3xl font-bold">Subscriptions</h1>
          <p className="text-lg">Manage subscription plans for this account</p>
        </div>
      </div>

      {products && products.length > 0 && (
        <>
          {/* Search Section */}
          <div className="relative">
            <SearchBar value={searchTerm} onChange={setSearchTerm} placeholder="Search subscriptions by name" />
          </div>
        </>
      )}

      {/* Content */}
      {isLoadingProducts ? (
        <div className="flex justify-center items-center gap-2">
          <Loader2 className="h-8 w-8 animate-spin" />
          <p className="text-sm">Loading subscriptions...</p>
        </div>
      ) : (
        renderResourceGrid(
          filteredProducts || [],
          (product) => (
            <ProductCard
              key={product.orbiusProductReferenceId}
              product={product}
              onManageServiceLine={(serviceLine) => onManageServiceLine(serviceLine)}
            />
          ),
          Package,
          searchTerm ? "No Matching Subscriptions" : "No Subscriptions",
          searchTerm ? "Try adjusting your search criteria." : "This account doesn't have any subscriptions yet."
        )
      )}
    </div>
  );
}
